/**
 * OrganicPostModal Component
 *
 * Modal for publishing campaigns as organic posts on social media platforms.
 * Uses the same account-selection UX as MultiAccountPublishModal (checkbox per
 * account, grouped by platform with logos and status badges).
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { apiClient } from '../../services/api';
import { useToast } from '../ui/Toast';
import { OAuthApiClient } from '../../lib/oauth/OAuthApiClient';
import { PlatformLogo, getPlatformName } from '../icons/PlatformLogos';
import type { PlatformKey } from '../icons/PlatformLogos';
import type { EnhancedOAuthConnection, Platform } from '../../lib/oauth/types';
import { useIsMobile } from '../../hooks/useMediaQuery';
import { BottomSheet } from '../ui/BottomSheet';
import { normalizeFilename } from '../../utils/mediaTagParser';

interface OrganicPostModalProps {
  isOpen: boolean;
  onClose: () => void;
  conversationId: string;
  campaignTitle: string;
  campaignConfig: any;
  onPublishComplete?: (results: PublishResult[]) => void;
  conversationMedia?: Array<{ filename: string; url: string; type: string }>;
  resolvedMedia?: Array<{ filename: string; url: string; media_type: string }>;
}

export interface PublishResult {
  platform: string;
  accountName: string;
  connectionId: string;
  status: 'pending' | 'publishing' | 'published' | 'failed';
  postsPublished: number;
  postUrls: string[];
  error?: string;
}

// Platforms that support organic posting
const ORGANIC_PLATFORMS: Platform[] = ['meta', 'twitter', 'pinterest'];

interface ExtractedCreative {
  index: number;
  primaryText: string;
  headline: string;
  mediaTags: string[];
}

interface FacebookPageInfo {
  page_id: string;
  page_name: string;
  page_category: string | null;
  is_default: boolean;
}

interface PinterestBoardInfo {
  board_id: string;
  board_name: string;
  description: string;
  privacy: string;
  pin_count: number;
  is_default: boolean;
}

/** Parse filename from a MEDIA tag like "[MEDIA:filename.jpg|type=image]" */
function parseMediaTagFilename(tag: string): string | null {
  const match = tag.match(/\[MEDIA:([^|\]]+)/);
  return match ? match[1].trim() : null;
}

/**
 * Extract all ad creatives from campaign config.
 * Mirrors backend's CampaignToPostConverter.extract_all_ads()
 */
/**
 * Extract ad creatives from campaign config, optionally filtered by platforms.
 * When platforms are specified, only creatives from those platforms are returned.
 */
function extractCreatives(campaignConfig: any, platforms?: string[]): ExtractedCreative[] {
  const creatives: ExtractedCreative[] = [];

  /** Extract ads from a platform config object that has ad_sets/ad_groups/line_items */
  function extractFromPlatformConfig(config: any) {
    const groups = [
      ...(config.ad_sets || []),
      ...(config.ad_groups || []),
      ...(config.line_items || []),
    ];
    for (const group of groups) {
      const ads = [
        ...(group.ads || []),
        ...(group.ad_variations || []),
      ];
      for (const ad of ads) {
        creatives.push({
          index: creatives.length,
          primaryText: Array.isArray(ad.primary_text)
            ? ad.primary_text[0] || ''
            : ad.primary_text || ad.text || ad.description || '',
          headline: Array.isArray(ad.headlines)
            ? ad.headlines[0] || ''
            : ad.headline || '',
          mediaTags: ad.media_tags || [],
        });
      }
    }
  }

  // Normalize platform filter (meta/facebook are the same)
  const platformFilter = platforms?.map(p => p === 'facebook' ? 'meta' : p);

  // Canonical format: { campaigns: [{ platform: "meta", platform_specific: { ad_sets: [...] } }] }
  const campaigns = campaignConfig?.campaigns || [];
  for (const campaign of campaigns) {
    // Skip campaigns not matching the platform filter
    if (platformFilter && campaign.platform && !platformFilter.includes(campaign.platform)) {
      continue;
    }
    const ps = campaign?.platform_specific;
    if (ps) {
      if (ps.ad_sets || ps.ad_groups || ps.line_items) {
        extractFromPlatformConfig(ps);
      } else {
        for (const platformConfig of Object.values(ps) as any[]) {
          if (platformConfig && typeof platformConfig === 'object') {
            extractFromPlatformConfig(platformConfig);
          }
          if (creatives.length > 0) break;
        }
      }
    }
    if (creatives.length > 0) return creatives;
  }

  // Legacy format: top-level keys like "meta", "google_ads" with ad_sets directly
  const legacyKeys = platformFilter || ['meta', 'google_ads', 'twitter', 'pinterest', 'tiktok'];
  for (const key of legacyKeys) {
    const config = campaignConfig?.[key];
    if (config && typeof config === 'object') {
      extractFromPlatformConfig(config);
      if (creatives.length > 0) return creatives;
    }
  }

  return creatives;
}

export const OrganicPostModal: React.FC<OrganicPostModalProps> = ({
  isOpen,
  onClose,
  conversationId,
  campaignTitle,
  campaignConfig,
  onPublishComplete,
  conversationMedia = [],
  resolvedMedia,
}) => {
  const { currentOrganization, user } = useAuth();
  const isMobile = useIsMobile();
  const { toast } = useToast();

  const [connections, setConnections] = useState<EnhancedOAuthConnection[]>([]);
  const [selectedConnections, setSelectedConnections] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [selectedCreatives, setSelectedCreatives] = useState<Set<number>>(new Set([0]));
  const [publishResults, setPublishResults] = useState<PublishResult[]>([]);
  const [currentStep, setCurrentStep] = useState<'select' | 'publishing' | 'complete'>('select');

  // Facebook Pages: { connectionId: { pages, loading } }
  const [facebookPages, setFacebookPages] = useState<Record<string, { pages: FacebookPageInfo[]; loading: boolean }>>({});
  const [selectedPages, setSelectedPages] = useState<Record<string, string>>({}); // connectionId -> pageId

  // Pinterest Boards: { connectionId: { boards, loading } }
  const [pinterestBoards, setPinterestBoards] = useState<Record<string, { boards: PinterestBoardInfo[]; loading: boolean }>>({});
  const [selectedBoards, setSelectedBoards] = useState<Record<string, string>>({}); // connectionId -> boardId
  const [newBoardName, setNewBoardName] = useState<Record<string, string>>({}); // connectionId -> board name input
  const [creatingBoard, setCreatingBoard] = useState<Record<string, boolean>>({}); // connectionId -> creating

  // Extract creatives from campaign config
  // Extract creatives filtered to selected platforms
  const selectedPlatforms = useMemo(() => {
    const platforms = new Set<string>();
    selectedConnections.forEach(connId => {
      const conn = connections.find(c => c.id === connId);
      if (conn) platforms.add(conn.platform);
    });
    return Array.from(platforms);
  }, [selectedConnections, connections]);

  const creatives = useMemo(
    () => selectedPlatforms.length > 0 ? extractCreatives(campaignConfig, selectedPlatforms) : [],
    [campaignConfig, selectedPlatforms]
  );

  // Build media lookup for thumbnails
  const getMediaUrl = useCallback((filename: string): string | null => {
    const normalized = normalizeFilename(filename);
    if (resolvedMedia) {
      const found = resolvedMedia.find(m => normalizeFilename(m.filename) === normalized);
      if (found) return found.url;
    }
    const found = conversationMedia.find(m => normalizeFilename(m.filename) === normalized);
    return found?.url || null;
  }, [resolvedMedia, conversationMedia]);

  // Get thumbnail URL for a creative
  const getCreativeThumbnail = useCallback((creative: ExtractedCreative): string | null => {
    for (const tag of creative.mediaTags) {
      const filename = parseMediaTagFilename(tag);
      if (filename) {
        const url = getMediaUrl(filename);
        if (url) return url;
      }
    }
    return null;
  }, [getMediaUrl]);

  const toggleCreative = (index: number) => {
    setSelectedCreatives((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const toggleAllCreatives = () => {
    if (selectedCreatives.size === creatives.length) {
      setSelectedCreatives(new Set([0])); // Always keep at least first selected
    } else {
      setSelectedCreatives(new Set(creatives.map(c => c.index)));
    }
  };

  const oauthClient = new OAuthApiClient();

  const fetchFacebookPages = useCallback(async (connectionId: string) => {
    // Use functional update to check if already fetched
    let alreadyFetched = false;
    setFacebookPages(prev => {
      if (prev[connectionId]) { alreadyFetched = true; return prev; }
      return { ...prev, [connectionId]: { pages: [], loading: true } };
    });
    if (alreadyFetched) return;
    try {
      const resp = await apiClient.get(`/api/v1/publisher/facebook-pages/${connectionId}`);
      const pages = resp.data.pages || [];
      setFacebookPages(prev => ({ ...prev, [connectionId]: { pages, loading: false } }));
      // Auto-select default page
      const defaultPage = pages.find((p: FacebookPageInfo) => p.is_default) || pages[0];
      if (defaultPage) {
        setSelectedPages(prev => ({ ...prev, [connectionId]: defaultPage.page_id }));
      }
    } catch {
      setFacebookPages(prev => ({ ...prev, [connectionId]: { pages: [], loading: false } }));
    }
  }, []);

  const fetchPinterestBoards = useCallback(async (connectionId: string) => {
    let alreadyFetched = false;
    setPinterestBoards(prev => {
      if (prev[connectionId]) { alreadyFetched = true; return prev; }
      return { ...prev, [connectionId]: { boards: [], loading: true } };
    });
    if (alreadyFetched) return;
    try {
      const resp = await apiClient.get(`/api/v1/publisher/pinterest-boards/${connectionId}`);
      const boards = resp.data.boards || [];
      setPinterestBoards(prev => ({ ...prev, [connectionId]: { boards, loading: false } }));
      const defaultBoard = boards.find((b: PinterestBoardInfo) => b.is_default) || boards[0];
      if (defaultBoard) {
        setSelectedBoards(prev => ({ ...prev, [connectionId]: defaultBoard.board_id }));
      }
    } catch {
      setPinterestBoards(prev => ({ ...prev, [connectionId]: { boards: [], loading: false } }));
    }
  }, []);

  const createPinterestBoard = useCallback(async (connectionId: string) => {
    const name = newBoardName[connectionId]?.trim();
    if (!name) return;
    setCreatingBoard(prev => ({ ...prev, [connectionId]: true }));
    try {
      const resp = await apiClient.post('/api/v1/publisher/pinterest-boards', {
        connection_id: connectionId,
        name,
      });
      const board = resp.data;
      setPinterestBoards(prev => {
        const existing = prev[connectionId]?.boards || [];
        return { ...prev, [connectionId]: { boards: [...existing, board], loading: false } };
      });
      setSelectedBoards(prev => ({ ...prev, [connectionId]: board.board_id }));
      setNewBoardName(prev => ({ ...prev, [connectionId]: '' }));
      toast.success(`Board "${name}" created`);
    } catch (error: any) {
      const msg = error.response?.data?.detail || 'Failed to create board';
      toast.error(msg);
    } finally {
      setCreatingBoard(prev => ({ ...prev, [connectionId]: false }));
    }
  }, [newBoardName, toast]);

  /**
   * Load OAuth connections for current organization, filtered to organic-capable platforms
   */
  const loadConnections = useCallback(async () => {
    if (!currentOrganization?.id) return;

    setLoading(true);
    try {
      const response = await oauthClient.getConnectionsForAllOrganizations();

      const orgConnections = response.connections_by_org[currentOrganization.id] || [];
      const relevantConnections = orgConnections.filter(
        (conn) =>
          ORGANIC_PLATFORMS.includes(conn.platform) &&
          !conn.revoked_at &&
          conn.status === 'valid'
      );

      setConnections(relevantConnections);

      // Auto-select default connections
      const defaultIds = relevantConnections
        .filter((c) => c.is_default)
        .map((c) => c.id);
      setSelectedConnections(new Set(defaultIds));

      // Fetch pages/boards for auto-selected connections
      for (const conn of relevantConnections) {
        if (conn.is_default && conn.platform === 'meta') {
          fetchFacebookPages(conn.id);
        }
        if (conn.is_default && conn.platform === 'pinterest') {
          fetchPinterestBoards(conn.id);
        }
      }
    } catch (error) {
      console.error('[OrganicPostModal] Failed to load connections:', error);
      setConnections([]);
    } finally {
      setLoading(false);
    }
  }, [currentOrganization?.id, fetchFacebookPages, fetchPinterestBoards]);

  useEffect(() => {
    if (isOpen) {
      setCurrentStep('select');
      setPublishResults([]);
      setSelectedCreatives(new Set([0]));
      setFacebookPages({});
      setSelectedPages({});
      setPinterestBoards({});
      setSelectedBoards({});
      setNewBoardName({});
      setCreatingBoard({});
      loadConnections();
    }
  }, [isOpen, loadConnections]);

  const toggleConnection = (connectionId: string) => {
    const conn = connections.find(c => c.id === connectionId);
    setSelectedConnections((prev) => {
      const next = new Set(prev);
      if (next.has(connectionId)) {
        next.delete(connectionId);
      } else {
        next.add(connectionId);
        // Fetch pages/boards when selecting a connection
        if (conn?.platform === 'meta') {
          fetchFacebookPages(connectionId);
        }
        if (conn?.platform === 'pinterest') {
          fetchPinterestBoards(connectionId);
        }
      }
      return next;
    });
  };

  /**
   * Group connections by platform for display
   */
  const getConnectionsByPlatform = () => {
    const grouped: Partial<Record<Platform, EnhancedOAuthConnection[]>> = {};
    connections.forEach((conn) => {
      if (!grouped[conn.platform]) grouped[conn.platform] = [];
      grouped[conn.platform]!.push(conn);
    });
    return grouped;
  };

  /**
   * Publish to selected accounts
   */
  const handlePublish = async () => {
    console.log('[OrganicPostModal] handlePublish called', {
      userId: user?.id,
      selectedConnections: Array.from(selectedConnections),
      connectionsCount: connections.length,
    });

    if (!user?.id) {
      console.warn('[OrganicPostModal] No user ID, aborting');
      toast.error('User not authenticated');
      return;
    }

    if (selectedConnections.size === 0) {
      toast.warning('Please select at least one account');
      return;
    }

    // Validate selected connections still exist
    const validSelected = Array.from(selectedConnections).filter((connId) =>
      connections.some((c) => c.id === connId)
    );

    if (validSelected.length === 0) {
      toast.error('Selected accounts are no longer available. Please re-select.');
      return;
    }

    setCurrentStep('publishing');
    setPublishing(true);

    // Build initial results
    const initialResults: PublishResult[] = validSelected.map((connId) => {
      const conn = connections.find((c) => c.id === connId)!;
      return {
        platform: conn.platform,
        accountName: conn.account_name || 'Unnamed Account',
        connectionId: connId,
        status: 'pending' as const,
        postsPublished: 0,
        postUrls: [],
      };
    });
    setPublishResults(initialResults);

    try {
      const selections = validSelected.map((connId) => {
        const conn = connections.find((c) => c.id === connId)!;
        const sel: any = {
          platform: conn.platform === 'meta' ? 'facebook' : conn.platform,
          connection_id: connId,
          account_id: conn.platform_account_id || conn.platform_user_id,
          account_name: conn.account_name,
        };
        // Include selected page_id for Meta connections
        if (conn.platform === 'meta' && selectedPages[connId]) {
          sel.page_id = selectedPages[connId];
          const pageInfo = facebookPages[connId]?.pages.find(p => p.page_id === selectedPages[connId]);
          if (pageInfo) sel.page_name = pageInfo.page_name;
        }
        // Include selected board_id for Pinterest connections
        if (conn.platform === 'pinterest' && selectedBoards[connId]) {
          sel.board_id = selectedBoards[connId];
        }
        return sel;
      });

      console.log('[OrganicPostModal] Sending publish request', { selections });

      const response = await apiClient.post('/api/v1/publisher/publish-organic', {
        conversation_id: conversationId,
        campaign_title: campaignTitle,
        campaign_config: campaignConfig,
        selected_variation_indices: Array.from(selectedCreatives).sort((a, b) => a - b),
        selections,
        user_id: user.id,
      });

      console.log('[OrganicPostModal] Publish response:', response.data);

      setPublishResults(response.data.results.map((r: any) => ({
        platform: r.platform,
        accountName: r.accountName || r.account_name || 'Unknown',
        connectionId: r.connection_id || '',
        status: r.status,
        postsPublished: r.postsPublished || r.posts_published || 0,
        postUrls: r.postUrls || r.post_urls || [],
        error: r.error,
      })));
      setCurrentStep('complete');
    } catch (error: any) {
      console.error('[OrganicPostModal] Publishing failed:', error);
      const errMsg = error.response?.data?.detail || error.response?.data?.message || error.message || 'Unknown error';

      // Show failed results in the complete step (same as MultiAccountPublishModal)
      setPublishResults((prev) =>
        prev.map((r) => ({
          ...r,
          status: 'failed' as const,
          error: errMsg,
        }))
      );
      setCurrentStep('complete');
    } finally {
      setPublishing(false);
    }
  };

  const handleClose = () => {
    if (publishing) return;
    if (currentStep === 'complete' && onPublishComplete) {
      onPublishComplete(publishResults);
    }
    onClose();
  };

  if (!isOpen) return null;

  const connectionsByPlatform = getConnectionsByPlatform();
  const selectedCount = selectedConnections.size;

  const renderContent = () => (
    <>
      {/* Header */}
      {!isMobile && (
        <div className="flex items-center justify-between px-6 py-5 border-b border-line dark:border-border-dark">
          <div>
            <h2 className="text-lg font-semibold text-ink">
              {currentStep === 'select' && 'Share as Organic Posts'}
              {currentStep === 'publishing' && 'Publishing Posts...'}
              {currentStep === 'complete' && 'Publishing Complete'}
            </h2>
            <p className="text-sm text-ink-2 mt-1">
              {currentStep === 'select' &&
                'Select accounts to publish your campaign creatives as regular posts'}
              {currentStep === 'publishing' && 'Please wait while we publish to selected accounts'}
              {currentStep === 'complete' && 'Organic post publishing results'}
            </p>
          </div>
          {!publishing && (
            <button
              onClick={handleClose}
              className="p-2 text-ink-3 hover:text-ink-2 rounded-lg hover:bg-inset transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      )}

      {/* Content */}
      <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
        {/* Step 1: Account Selection */}
        {currentStep === 'select' && (
          <>
            {loading ? (
              <div className="flex justify-center items-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                <span className="ml-3 text-ink-2">Loading accounts...</span>
              </div>
            ) : connections.length === 0 ? (
              <div className="text-center py-12 bg-ground rounded-lg border-2 border-dashed border-line-2 dark:border-border-dark">
                <svg className="mx-auto w-16 h-16 text-ink-3 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
                <h3 className="text-lg font-medium text-ink mb-2">
                  No Connected Accounts
                </h3>
                <p className="text-sm text-ink-2 mb-4">
                  Connect a social media account to publish organic posts
                </p>
                <button
                  onClick={() => {
                    handleClose();
                    window.location.href = '/settings/businesses/active/connections';
                  }}
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                >
                  Connect Accounts
                </button>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Account list grouped by platform */}
                {Object.entries(connectionsByPlatform).map(([platform, platformConnections]) => {
                  if (!platformConnections || platformConnections.length === 0) return null;
                  const platformKey = platform as Platform;

                  return (
                    <div key={platform}>
                      <div className="flex items-center gap-3 mb-3">
                        <PlatformLogo platform={platformKey as PlatformKey} size="sm" />
                        <h3 className="font-semibold text-ink">
                          {getPlatformName(platformKey)}
                        </h3>
                        <span className="text-xs text-ink-3">
                          ({platformConnections.length} account{platformConnections.length !== 1 ? 's' : ''})
                        </span>
                      </div>

                      <div className="space-y-2 ml-11">
                        {platformConnections.map((connection) => {
                          const isSelected = selectedConnections.has(connection.id);
                          const isMeta = connection.platform === 'meta';
                          const isPinterest = connection.platform === 'pinterest';
                          const connPages = isMeta ? facebookPages[connection.id] : null;
                          const connBoards = isPinterest ? pinterestBoards[connection.id] : null;
                          const pagesLoaded = isMeta && isSelected && connPages && !connPages.loading && connPages.pages.length > 0;
                          const showPagePicker = pagesLoaded && connPages!.pages.length > 1;
                          const showPageConfirm = pagesLoaded && connPages!.pages.length === 1;
                          const boardsFetched = isPinterest && isSelected && connBoards && !connBoards.loading;
                          const boardsLoaded = boardsFetched && connBoards!.boards.length > 0;
                          const showBoardPicker = boardsLoaded && connBoards!.boards.length > 1;
                          const showBoardConfirm = boardsLoaded && connBoards!.boards.length === 1;
                          const showBoardCreate = isPinterest && isSelected && boardsFetched && connBoards!.boards.length === 0;
                          const hasPageSubSection = showPagePicker || showPageConfirm || (isMeta && isSelected && connPages?.loading)
                            || showBoardPicker || showBoardConfirm || showBoardCreate || (isPinterest && isSelected && connBoards?.loading);

                          return (
                            <div key={connection.id}>
                              <label
                                className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                                  isSelected
                                    ? 'bg-work-soft border-work/40'
                                    : 'bg-ground border-line dark:border-border-dark hover:border-line-2'
                                } ${hasPageSubSection ? 'rounded-b-none border-b-0' : ''}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => toggleConnection(connection.id)}
                                  className="w-5 h-5 text-primary border-line-2 rounded focus:ring-2 focus:ring-primary"
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium text-ink">
                                      {connection.account_name || 'Unnamed Account'}
                                    </span>
                                    {connection.is_default && (
                                      <span className="px-2 py-0.5 bg-work-soft text-work text-xs font-medium rounded">
                                        Default
                                      </span>
                                    )}
                                    {connection.status === 'valid' && (
                                      <span className="flex items-center gap-1 text-xs text-ok">
                                        <div className="w-1.5 h-1.5 bg-ok rounded-full"></div>
                                        Active
                                      </span>
                                    )}
                                    {connection.requires_reauth && (
                                      <span className="flex items-center gap-1 text-xs text-orange-600">
                                        <div className="w-1.5 h-1.5 bg-orange-500 rounded-full"></div>
                                        Reauth Required
                                      </span>
                                    )}
                                  </div>
                                  {connection.connection_label && (
                                    <p className="text-xs text-ink-2 mt-0.5">
                                      {connection.connection_label}
                                    </p>
                                  )}
                                </div>
                              </label>
                              {/* Page loading spinner */}
                              {isMeta && isSelected && connPages?.loading && (
                                <div className="px-4 py-2 bg-work-soft/50 border-2 border-t-0 border-work/40 rounded-b-lg">
                                  <div className="flex items-center gap-2 text-xs text-ink-3">
                                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-primary"></div>
                                    Loading pages...
                                  </div>
                                </div>
                              )}
                              {/* Single page confirmation */}
                              {showPageConfirm && (
                                <div className="px-4 py-2.5 bg-work-soft/50 border-2 border-t-0 border-work/40 rounded-b-lg">
                                  <div className="flex items-center gap-2 text-xs text-ink-2">
                                    <svg className="w-3.5 h-3.5 text-work" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                                    </svg>
                                    Posting to <span className="font-medium text-ink">{connPages!.pages[0].page_name}</span>
                                  </div>
                                </div>
                              )}
                              {/* Page selector for Meta connections with multiple pages */}
                              {showPagePicker && (
                                <div className="px-4 py-3 bg-work-soft/50 border-2 border-t-0 border-work/40 rounded-b-lg">
                                  <p className="text-xs font-medium text-ink-2 mb-2">
                                    Post to Page:
                                  </p>
                                  <div className="space-y-1.5">
                                    {connPages!.pages.map((page) => (
                                      <label
                                        key={page.page_id}
                                        className={`flex items-center gap-2.5 px-3 py-2 rounded-md cursor-pointer transition-colors ${
                                          selectedPages[connection.id] === page.page_id
                                            ? 'bg-card shadow-sm'
                                            : 'hover:bg-card/60'
                                        }`}
                                      >
                                        <input
                                          type="radio"
                                          name={`page-${connection.id}`}
                                          checked={selectedPages[connection.id] === page.page_id}
                                          onChange={() => setSelectedPages(prev => ({ ...prev, [connection.id]: page.page_id }))}
                                          className="w-4 h-4 text-primary border-line-2 focus:ring-2 focus:ring-primary"
                                        />
                                        <div className="flex-1 min-w-0">
                                          <span className="text-sm font-medium text-ink">
                                            {page.page_name}
                                          </span>
                                          {page.page_category && (
                                            <span className="ml-2 text-xs text-ink-3">
                                              {page.page_category}
                                            </span>
                                          )}
                                        </div>
                                        {page.is_default && (
                                          <span className="px-1.5 py-0.5 bg-work-soft text-work text-[10px] font-medium rounded">
                                            Default
                                          </span>
                                        )}
                                      </label>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {/* Board loading spinner for Pinterest */}
                              {isPinterest && isSelected && connBoards?.loading && (
                                <div className="px-4 py-2 bg-bad-soft/50 border-2 border-t-0 border-work/40 rounded-b-lg">
                                  <div className="flex items-center gap-2 text-xs text-ink-3">
                                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-primary"></div>
                                    Loading boards...
                                  </div>
                                </div>
                              )}
                              {/* Single board confirmation for Pinterest */}
                              {showBoardConfirm && (
                                <div className="px-4 py-2.5 bg-bad-soft/50 border-2 border-t-0 border-work/40 rounded-b-lg">
                                  <div className="flex items-center gap-2 text-xs text-ink-2">
                                    <svg className="w-3.5 h-3.5 text-bad" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7" />
                                    </svg>
                                    Pinning to <span className="font-medium text-ink">{connBoards!.boards[0].board_name}</span>
                                  </div>
                                </div>
                              )}
                              {/* Board selector for Pinterest connections with multiple boards */}
                              {showBoardPicker && (
                                <div className="px-4 py-3 bg-bad-soft/50 border-2 border-t-0 border-work/40 rounded-b-lg">
                                  <p className="text-xs font-medium text-ink-2 mb-2">
                                    Pin to Board:
                                  </p>
                                  <div className="space-y-1.5">
                                    {connBoards!.boards.map((board) => (
                                      <label
                                        key={board.board_id}
                                        className={`flex items-center gap-2.5 px-3 py-2 rounded-md cursor-pointer transition-colors ${
                                          selectedBoards[connection.id] === board.board_id
                                            ? 'bg-card shadow-sm'
                                            : 'hover:bg-card/60'
                                        }`}
                                      >
                                        <input
                                          type="radio"
                                          name={`board-${connection.id}`}
                                          checked={selectedBoards[connection.id] === board.board_id}
                                          onChange={() => setSelectedBoards(prev => ({ ...prev, [connection.id]: board.board_id }))}
                                          className="w-4 h-4 text-primary border-line-2 focus:ring-2 focus:ring-primary"
                                        />
                                        <div className="flex-1 min-w-0">
                                          <span className="text-sm font-medium text-ink">
                                            {board.board_name}
                                          </span>
                                          <span className="ml-2 text-xs text-ink-3">
                                            {board.pin_count} pin{board.pin_count !== 1 ? 's' : ''}
                                          </span>
                                        </div>
                                        {board.is_default && (
                                          <span className="px-1.5 py-0.5 bg-bad-soft text-bad text-[10px] font-medium rounded">
                                            Default
                                          </span>
                                        )}
                                      </label>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {/* Create board for Pinterest - shown when no boards exist */}
                              {showBoardCreate && (
                                <div className="px-4 py-3 bg-bad-soft/50 border-2 border-t-0 border-work/40 rounded-b-lg">
                                  <p className="text-xs text-ink-2 mb-2">
                                    No boards found. Create one to start pinning:
                                  </p>
                                  <div className="flex gap-2">
                                    <input
                                      type="text"
                                      placeholder="Board name"
                                      value={newBoardName[connection.id] || ''}
                                      onChange={(e) => setNewBoardName(prev => ({ ...prev, [connection.id]: e.target.value }))}
                                      onKeyDown={(e) => { if (e.key === 'Enter') createPinterestBoard(connection.id); }}
                                      className="flex-1 px-3 py-1.5 text-sm border border-line-2 rounded-md bg-card text-ink focus:ring-2 focus:ring-primary focus:border-primary"
                                      disabled={creatingBoard[connection.id]}
                                    />
                                    <button
                                      onClick={() => createPinterestBoard(connection.id)}
                                      disabled={!newBoardName[connection.id]?.trim() || creatingBoard[connection.id]}
                                      className="px-3 py-1.5 text-sm bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                                    >
                                      {creatingBoard[connection.id] ? 'Creating...' : 'Create Board'}
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}

                {/* Creative Picker */}
                {creatives.length > 0 && (
                  <div className="border border-line dark:border-border-dark rounded-lg overflow-hidden">
                    <div className="px-4 py-3 bg-ground border-b border-line dark:border-border-dark">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedCreatives.size === creatives.length}
                          onChange={toggleAllCreatives}
                          className="w-4 h-4 text-primary border-line-2 rounded focus:ring-2 focus:ring-primary"
                        />
                        <span className="ml-2.5 text-sm font-medium text-ink-2">
                          Select All ({creatives.length} creative{creatives.length !== 1 ? 's' : ''})
                        </span>
                      </label>
                    </div>
                    <div className="divide-y divide-line dark:divide-border-dark max-h-60 overflow-y-auto">
                      {creatives.map((creative) => {
                        const thumbnailUrl = getCreativeThumbnail(creative);
                        const previewText = creative.primaryText || creative.headline || 'No text content';
                        const truncated = previewText.length > 80
                          ? previewText.slice(0, 80) + '...'
                          : previewText;

                        return (
                          <label
                            key={creative.index}
                            className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${
                              selectedCreatives.has(creative.index)
                                ? 'bg-work-soft/50'
                                : 'hover:bg-ground'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={selectedCreatives.has(creative.index)}
                              onChange={() => toggleCreative(creative.index)}
                              className="w-4 h-4 text-primary border-line-2 rounded focus:ring-2 focus:ring-primary flex-shrink-0"
                            />
                            {thumbnailUrl ? (
                              <img
                                src={thumbnailUrl}
                                alt=""
                                className="w-10 h-10 rounded-md object-cover flex-shrink-0 bg-inset"
                              />
                            ) : (
                              <div className="w-10 h-10 rounded-md flex-shrink-0 bg-inset flex items-center justify-center">
                                <svg className="w-5 h-5 text-ink-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-ink">
                                Creative {creative.index + 1}
                              </p>
                              <p className="text-xs text-ink-3 truncate">
                                {truncated}
                              </p>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Step 2: Publishing Progress */}
        {currentStep === 'publishing' && (
          <div className="space-y-3">
            {publishResults.map((result) => (
              <div
                key={result.connectionId}
                className="flex items-center gap-3 p-4 bg-ground rounded-lg border border-line dark:border-border-dark"
              >
                <PlatformLogo platform={(result.platform === 'facebook' ? 'meta' : result.platform) as PlatformKey} size="sm" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-ink">{result.accountName}</p>
                  <p className="text-xs text-ink-2 capitalize">{result.platform}</p>
                </div>
                <div className="flex items-center gap-2">
                  {result.status === 'pending' && (
                    <span className="text-sm text-ink-3">Waiting...</span>
                  )}
                  {result.status === 'publishing' && (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
                      <span className="text-sm text-primary">Publishing...</span>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Step 3: Results */}
        {currentStep === 'complete' && (
          <div className="space-y-4">
            {/* Summary Stats */}
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="p-4 bg-work-soft rounded-lg border border-work/30">
                <p className="text-2xl font-bold text-blue-900">
                  {publishResults.length}
                </p>
                <p className="text-sm text-primary">Total Accounts</p>
              </div>
              <div className="p-4 bg-ok-soft rounded-lg border border-ok/30">
                <p className="text-2xl font-bold text-green-900">
                  {publishResults.filter((r) => r.status === 'published').length}
                </p>
                <p className="text-sm text-ok">Successful</p>
              </div>
              <div className="p-4 bg-bad-soft rounded-lg border border-bad/30">
                <p className="text-2xl font-bold text-red-900">
                  {publishResults.filter((r) => r.status === 'failed').length}
                </p>
                <p className="text-sm text-bad">Failed</p>
              </div>
            </div>

            {/* Detailed Results */}
            <div className="space-y-3">
              {publishResults.map((result, idx) => (
                <div
                  key={idx}
                  className={`p-4 rounded-lg border-2 ${
                    result.status === 'published'
                      ? 'bg-ok-soft border-ok/30'
                      : 'bg-bad-soft border-bad/30'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <PlatformLogo platform={(result.platform === 'facebook' ? 'meta' : result.platform) as PlatformKey} size="sm" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium text-ink">
                          {result.accountName}
                        </p>
                        {result.status === 'published' && (
                          <svg className="w-5 h-5 text-ok" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        )}
                        {result.status === 'failed' && (
                          <svg className="w-5 h-5 text-bad" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      {result.status === 'published' && result.postsPublished > 0 && (
                        <p className="text-xs text-ok">
                          {result.postsPublished} post{result.postsPublished !== 1 ? 's' : ''} published
                        </p>
                      )}

                      {/* Links to posts */}
                      {result.postUrls && result.postUrls.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {result.postUrls.map((url, urlIdx) => (
                            <a
                              key={urlIdx}
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block text-sm text-primary hover:underline"
                            >
                              View post {urlIdx + 1} ↗
                            </a>
                          ))}
                        </div>
                      )}

                      {result.error && (
                        <p className="text-sm text-bad mt-2">{result.error}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between px-6 py-4 border-t border-line dark:border-border-dark rounded-b-2xl bg-ground">
        {currentStep === 'select' && (
          <>
            <p className="text-sm text-ink-2">
              {selectedCreatives.size} creative{selectedCreatives.size !== 1 ? 's' : ''} to {selectedCount} account{selectedCount !== 1 ? 's' : ''}
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleClose}
                className="px-6 py-2 bg-inset text-ink-2 font-medium rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handlePublish}
                disabled={selectedCount === 0 || selectedCreatives.size === 0 || loading}
                className="px-6 py-2 bg-primary text-white font-medium rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Post {selectedCreatives.size} Creative{selectedCreatives.size !== 1 ? 's' : ''}
              </button>
            </div>
          </>
        )}

        {currentStep === 'publishing' && (
          <div className="flex-1 text-center">
            <p className="text-sm text-ink-2">
              Publishing in progress... Please do not close this window
            </p>
          </div>
        )}

        {currentStep === 'complete' && (
          <>
            <div className="text-sm text-ink-2">
              {publishResults.every((r) => r.status === 'published') ? (
                <span className="text-ok font-medium">
                  All posts published successfully
                </span>
              ) : (
                <span className="text-orange-600 font-medium">
                  Some posts failed to publish
                </span>
              )}
            </div>
            <button
              onClick={handleClose}
              className="px-6 py-2 bg-primary text-white font-medium rounded-lg hover:bg-primary/90 transition-colors"
            >
              Close
            </button>
          </>
        )}
      </div>
    </>
  );

  // Mobile: Use BottomSheet
  if (isMobile) {
    return (
      <BottomSheet
        isOpen={isOpen}
        onClose={onClose}
        title="Share as Posts"
        snapPoints={[0.5, 0.9]}
        initialSnap={1}
      >
        <BottomSheet.Content>
          {renderContent()}
        </BottomSheet.Content>
      </BottomSheet>
    );
  }

  // Desktop: Use standard modal
  return (
    <>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[1000] animate-fadeIn" onClick={handleClose} />
      <div className="fixed inset-0 z-[1001] overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <div
            className="relative w-full max-w-4xl bg-card dark:bg-card-dark rounded-2xl shadow-2xl border border-line dark:border-border-dark animate-slideUp"
            onClick={(e) => e.stopPropagation()}
          >
            {renderContent()}
          </div>
        </div>
      </div>
    </>
  );
};

export default OrganicPostModal;
